import { state } from '../state';
import { get, post } from '../api';
import { showModal, closeModal, modalFrame, confirmModal } from '../components/modal';
import { showToast } from '../components/toast';
import { renderMarkdown } from './chat';
import { iconPlus, iconFileText, iconEye, iconCheck, iconX } from '../icons';
import type { Agreement, AgreementStatus, AgreementRecipient, AgreementDetail, AgreementSigningToken } from '../types';

// ── Status pill colours — DocuSign exact match ──

const STATUS_CONFIG: Record<AgreementStatus, { label: string; cls: string }> = {
  draft:    { label: 'Draft',    cls: 'ag-pill-draft' },
  sent:     { label: 'Sent',     cls: 'ag-pill-sent' },
  viewed:   { label: 'Viewed',   cls: 'ag-pill-viewed' },
  signed:   { label: 'Signed',   cls: 'ag-pill-signed' },
  declined: { label: 'Declined', cls: 'ag-pill-declined' },
  voided:   { label: 'Voided',   cls: 'ag-pill-voided' },
};

function statusPill(status: AgreementStatus): string {
  const c = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft;
  return `<span class="ag-status-pill ${c.cls}">${c.label}</span>`;
}

function escHtml(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

function recipientStatusPill(status: string): string {
  const map: Record<string, string> = {
    pending: 'ag-rpill-pending', sent: 'ag-rpill-sent', viewed: 'ag-rpill-viewed',
    signed: 'ag-rpill-signed', declined: 'ag-rpill-declined',
  };
  return `<span class="ag-recipient-pill ${map[status] ?? 'ag-rpill-pending'}">${escHtml(status)}</span>`;
}


// ── Document list view (DocuSign 1:1 table) ──

export function renderAgreements(body: HTMLElement): void {
  const sid = state.currentServer;
  const agreements = sid
    ? state.agreements.filter(a => a.server_id === sid)
    : state.agreements;
  const reload = (): void => {
    get<Agreement[]>(`/api/agreements${sid ? `?server_id=${encodeURIComponent(sid)}` : ''}`)
      .then(data => { state.agreements = data; renderAgreements(body); })
      .catch(() => renderAgreements(body));
  };

  body.innerHTML = `
    <div class="channel-header">
      <h1>${iconFileText()} Agreements</h1>
      <div class="channel-actions">
        <button class="btn btn-primary" id="ag-new">${iconPlus()} New Agreement</button>
      </div>
    </div>
    <div class="ag-table-wrap">
      <table class="ag-table">
        <thead>
          <tr>
            <th>Subject</th>
            <th>Recipients</th>
            <th>Status</th>
            <th>Sent</th>
            <th>Last Activity</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${agreements.length ? agreements.map(a => `
            <tr class="ag-row" data-id="${a.id}">
              <td class="ag-subject">${escHtml(a.title)}</td>
              <td class="ag-recipients-cell">-</td>
              <td>${statusPill(a.status ?? 'draft')}</td>
              <td class="ag-date">${fmtDate(a.sent_at)}</td>
              <td class="ag-date">${fmtDate(a.updated_at)}</td>
              <td class="ag-actions-cell">
                <button class="btn btn-sm btn-ghost ag-view-btn" data-id="${a.id}" title="View details">${iconEye()}</button>
                ${a.status === 'draft' ? `<button class="btn btn-sm btn-ghost ag-send-btn" data-id="${a.id}" title="Send">Send</button>` : ''}
                ${a.status !== 'voided' && a.status !== 'signed' ? `<button class="btn btn-sm btn-ghost ag-void-btn" data-id="${a.id}" title="Void">${iconX()}</button>` : ''}
              </td>
            </tr>
          `).join('') : `
            <tr><td colspan="6" class="ag-empty">
              <div class="empty-state"><p>No agreements yet. Create one to get started.</p></div>
            </td></tr>
          `}
        </tbody>
      </table>
    </div>`;

  // Wire events
  document.getElementById('ag-new')?.addEventListener('click', () => openNewAgreementWizard(reload));

  body.querySelectorAll<HTMLElement>('.ag-view-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = btn.dataset['id'];
      if (id) openDetailView(id, reload);
    });
  });

  body.querySelectorAll<HTMLElement>('.ag-send-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = btn.dataset['id'];
      if (id) confirmModal('Send Agreement', 'Send this agreement to all recipients?', () => {
        post<Agreement>(`/api/agreements/${id}/send`, {})
          .then(() => { showToast('Agreement sent', 'ok'); reload(); })
          .catch(er => showToast(String(er), 'err'));
      });
    });
  });

  body.querySelectorAll<HTMLElement>('.ag-void-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = btn.dataset['id'];
      if (id) confirmModal('Void Agreement', 'This action is irreversible. Void this agreement?', () => {
        post<Agreement>('/api/agreements/void', { id })
          .then(() => { showToast('Agreement voided', 'ok'); reload(); })
          .catch(er => showToast(String(er), 'err'));
      });
    });
  });

  // Row click → detail
  body.querySelectorAll<HTMLElement>('.ag-row').forEach(row => {
    row.addEventListener('click', () => {
      const id = row.dataset['id'];
      if (id) openDetailView(id, reload);
    });
  });

  // Async-load recipient counts per agreement
  for (const a of agreements) {
    get<AgreementRecipient[]>(`/api/agreements/${a.id}/recipients`).then(recs => {
      const cell = body.querySelector<HTMLElement>(`.ag-row[data-id="${a.id}"] .ag-recipients-cell`);
      if (cell) {
        if (recs.length === 0) { cell.textContent = '-'; }
        else {
          const names = recs.map(r => r.display_name || r.email || '(unknown)').join(', ');
          cell.textContent = names.length > 40 ? `${recs.length} recipients` : names;
        }
      }
    }).catch(() => { /* ignore */ });
  }
}

// ── 3-Step New Agreement Wizard ──

function openNewAgreementWizard(reload: () => void): void {
  let step = 1;
  let docTitle = '';
  let docBody = '';
  const recipients: Array<{ display_name: string; email: string; role: string }> = [];
  let signatureField = true;
  let dateField = true;

  function renderWizard(): void {
    const stepIndicator = `
      <div class="ag-wizard-steps">
        <div class="ag-wizard-step ${step >= 1 ? 'active' : ''}"><span class="ag-step-num">1</span> Document</div>
        <div class="ag-wizard-step ${step >= 2 ? 'active' : ''}"><span class="ag-step-num">2</span> Recipients</div>
        <div class="ag-wizard-step ${step >= 3 ? 'active' : ''}"><span class="ag-step-num">3</span> Fields</div>
      </div>`;

    let content = '';
    let footer = '';

    if (step === 1) {
      content = `
        ${stepIndicator}
        <div class="form-group">
          <label class="form-label" for="wiz-title">Subject *</label>
          <input id="wiz-title" class="form-input" value="${escHtml(docTitle)}" placeholder="Agreement subject line">
        </div>
        <div class="form-group">
          <label class="form-label" for="wiz-body">Document Content (Markdown)</label>
          <textarea id="wiz-body" class="form-input ag-editor" rows="12" placeholder="Type or paste your agreement content here...">${escHtml(docBody)}</textarea>
        </div>
        <div class="ag-preview-toggle">
          <button class="btn btn-sm btn-ghost" id="wiz-preview-btn">Preview</button>
        </div>
        <div id="wiz-preview" class="ag-md-preview" style="display:none"></div>`;
      footer = `
        <button class="btn btn-secondary" id="wiz-cancel">Cancel</button>
        <button class="btn btn-primary" id="wiz-next">Next: Add Recipients</button>`;
    } else if (step === 2) {
      content = `
        ${stepIndicator}
        <div class="ag-recipient-list">
          <h3>Recipients</h3>
          ${recipients.map((r, i) => `
            <div class="ag-recipient-row">
              <span class="ag-recipient-name">${escHtml(r.display_name || r.email)}</span>
              <span class="ag-recipient-role">${escHtml(r.role)}</span>
              <button class="btn btn-sm btn-ghost ag-remove-recipient" data-idx="${i}">${iconX()}</button>
            </div>
          `).join('')}
          ${recipients.length === 0 ? '<p class="ag-hint">Add at least one signer to continue.</p>' : ''}
        </div>
        <div class="ag-add-recipient-form">
          <div class="form-row">
            <div class="form-group" style="flex:1">
              <label class="form-label" for="wiz-rname">Name</label>
              <input id="wiz-rname" class="form-input" placeholder="Recipient name">
            </div>
            <div class="form-group" style="flex:1">
              <label class="form-label" for="wiz-remail">Email</label>
              <input id="wiz-remail" class="form-input" type="email" placeholder="email@example.com">
            </div>
            <div class="form-group" style="flex:0 0 120px">
              <label class="form-label" for="wiz-rrole">Role</label>
              <select id="wiz-rrole" class="form-input">
                <option value="signer">Signer</option>
                <option value="cc">CC</option>
                <option value="approver">Approver</option>
              </select>
            </div>
          </div>
          <button class="btn btn-secondary" id="wiz-add-recipient">${iconPlus()} Add Recipient</button>
          <div class="ag-people-hint">
            <strong>Or pick from people:</strong>
            <div class="ag-people-chips" id="wiz-people-chips">
              ${state.people.filter(p => p.status === 'active').slice(0, 20).map(p =>
                `<button class="ag-person-chip" data-pid="${p.id}" data-name="${escHtml(p.name || p.email)}" data-email="${escHtml(p.email)}">${escHtml(p.name || p.email || p.id.slice(0, 8))}</button>`
              ).join('')}
            </div>
          </div>
        </div>`;
      footer = `
        <button class="btn btn-secondary" id="wiz-back">Back</button>
        <button class="btn btn-primary" id="wiz-next" ${recipients.filter(r => r.role === 'signer').length === 0 ? 'disabled' : ''}>Next: Place Fields</button>`;
    } else {
      content = `
        ${stepIndicator}
        <div class="ag-fields-config">
          <h3>Signature Fields</h3>
          <p class="ag-hint">Select which fields recipients must complete. (Drag-and-drop field placement coming in v2.)</p>
          <label class="ag-field-check">
            <input type="checkbox" id="wiz-field-sig" ${signatureField ? 'checked' : ''}> Signature (type-to-sign)
          </label>
          <label class="ag-field-check">
            <input type="checkbox" id="wiz-field-date" ${dateField ? 'checked' : ''}> Date Signed
          </label>
        </div>
        <div class="ag-summary">
          <h3>Summary</h3>
          <p><strong>Subject:</strong> ${escHtml(docTitle)}</p>
          <p><strong>Recipients:</strong> ${recipients.map(r => `${escHtml(r.display_name || r.email)} (${r.role})`).join(', ')}</p>
        </div>`;
      footer = `
        <button class="btn btn-secondary" id="wiz-back">Back</button>
        <button class="btn btn-primary" id="wiz-send">Send</button>
        <button class="btn btn-secondary" id="wiz-draft">Save as Draft</button>`;
    }

    showModal(modalFrame('New Agreement', content, footer), { wide: true });
    wireWizardEvents();
  }

  function wireWizardEvents(): void {
    document.getElementById('modal-close')?.addEventListener('click', closeModal);
    document.getElementById('wiz-cancel')?.addEventListener('click', closeModal);

    // Step 1
    document.getElementById('wiz-preview-btn')?.addEventListener('click', () => {
      const prev = document.getElementById('wiz-preview');
      const tb = document.getElementById('wiz-body') as HTMLTextAreaElement | null;
      if (prev && tb) {
        prev.style.display = prev.style.display === 'none' ? 'block' : 'none';
        prev.innerHTML = renderMarkdown(tb.value);
      }
    });

    // Step navigation
    document.getElementById('wiz-next')?.addEventListener('click', () => {
      if (step === 1) {
        const titleEl = document.getElementById('wiz-title') as HTMLInputElement | null;
        const bodyEl = document.getElementById('wiz-body') as HTMLTextAreaElement | null;
        docTitle = titleEl?.value.trim() ?? '';
        docBody = bodyEl?.value ?? '';
        if (!docTitle) { showToast('Subject is required', 'err'); return; }
        step = 2;
      } else if (step === 2) {
        if (recipients.filter(r => r.role === 'signer').length === 0) {
          showToast('Add at least one signer', 'err'); return;
        }
        step = 3;
      }
      renderWizard();
    });

    document.getElementById('wiz-back')?.addEventListener('click', () => {
      if (step === 2) { step = 1; }
      else if (step === 3) { step = 2; }
      renderWizard();
    });

    // Step 2 — add recipient
    document.getElementById('wiz-add-recipient')?.addEventListener('click', () => {
      const nameEl = document.getElementById('wiz-rname') as HTMLInputElement | null;
      const emailEl = document.getElementById('wiz-remail') as HTMLInputElement | null;
      const roleEl = document.getElementById('wiz-rrole') as HTMLSelectElement | null;
      const name = nameEl?.value.trim() ?? '';
      const email = emailEl?.value.trim() ?? '';
      const role = roleEl?.value ?? 'signer';
      if (!name && !email) { showToast('Name or email required', 'err'); return; }
      recipients.push({ display_name: name, email, role });
      renderWizard();
    });

    // Remove recipient
    document.querySelectorAll<HTMLElement>('.ag-remove-recipient').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset['idx'] ?? '-1', 10);
        if (idx >= 0) { recipients.splice(idx, 1); renderWizard(); }
      });
    });

    // People chips
    document.getElementById('wiz-people-chips')?.addEventListener('click', (e) => {
      const chip = (e.target as HTMLElement).closest<HTMLElement>('.ag-person-chip');
      if (!chip) return;
      const name = chip.dataset['name'] ?? '';
      const email = chip.dataset['email'] ?? '';
      if (!recipients.some(r => r.email === email && r.display_name === name)) {
        recipients.push({ display_name: name, email, role: 'signer' });
        renderWizard();
      }
    });

    // Step 3 — fields
    document.getElementById('wiz-field-sig')?.addEventListener('change', (e) => {
      signatureField = (e.target as HTMLInputElement).checked;
    });
    document.getElementById('wiz-field-date')?.addEventListener('change', (e) => {
      dateField = (e.target as HTMLInputElement).checked;
    });

    // Step 3 — Send or Save Draft
    document.getElementById('wiz-send')?.addEventListener('click', () => submitWizard(true, reload));
    document.getElementById('wiz-draft')?.addEventListener('click', () => submitWizard(false, reload));
  }

  function submitWizard(sendImmediately: boolean, reloadFn: () => void): void {
    const recips = recipients.map((r, i) => ({
      display_name: r.display_name,
      email: r.email,
      role: r.role,
      order_num: i,
    }));

    post<Agreement>('/api/agreements/add', {
      title: docTitle,
      body: docBody,
      server_id: state.currentServer,
      created_by: state.ownerId || 'owner',
      cascades_to: 'none',
      recipients: recips,
    }).then(ag => {
      if (sendImmediately) {
        return post<Agreement>(`/api/agreements/${ag.id}/send`, {}).then(() => {
          closeModal();
          showToast('Agreement sent', 'ok');
          reloadFn();
        });
      } else {
        closeModal();
        showToast('Agreement saved as draft', 'ok');
        reloadFn();
      }
    }).catch(e => showToast(String(e), 'err'));
  }

  renderWizard();
}

// ── Detail View (Document + Envelope Info) ──

function openDetailView(agreementId: string, reload: () => void): void {
  get<AgreementDetail>(`/api/agreements/${agreementId}/detail`).then(detail => {
    const ag = detail.agreement;
    const recs = detail.recipients;
    const sigs = detail.signatures;
    const trail = detail.audit_trail;

    const content = `
      <div class="ag-detail">
        <div class="ag-detail-main">
          <div class="ag-detail-doc">
            <h2>${escHtml(ag.title)}</h2>
            <div class="ag-doc-body">${renderMarkdown(ag.body)}</div>
          </div>
        </div>
        <div class="ag-detail-sidebar">
          <div class="ag-envelope-info">
            <h3>Envelope Info</h3>
            <div class="ag-info-row"><span>Status</span>${statusPill(ag.status ?? 'draft')}</div>
            <div class="ag-info-row"><span>Created</span><span>${fmtDateTime(ag.created_at)}</span></div>
            <div class="ag-info-row"><span>Sent</span><span>${fmtDateTime(ag.sent_at)}</span></div>
            ${ag.voided_at ? `<div class="ag-info-row"><span>Voided</span><span>${fmtDateTime(ag.voided_at)}</span></div>` : ''}
          </div>

          <div class="ag-recipients-section">
            <h3>Recipients</h3>
            ${recs.map(r => `
              <div class="ag-recipient-detail">
                <div class="ag-recipient-info">
                  <strong>${escHtml(r.display_name || r.email || 'Unknown')}</strong>
                  <span class="ag-recipient-role-tag">${escHtml(r.role)}</span>
                </div>
                <div class="ag-recipient-status-row">
                  ${recipientStatusPill(r.status)}
                  ${r.signed_at ? `<span class="ag-signed-date">${iconCheck()} ${fmtDateTime(r.signed_at)}</span>` : ''}
                  ${r.declined_at ? `<span class="ag-declined-date">${iconX()} ${fmtDateTime(r.declined_at)}</span>` : ''}
                </div>
              </div>
            `).join('')}
            ${recs.length === 0 ? '<p class="ag-hint">No recipients added yet.</p>' : ''}
          </div>

          ${sigs.length > 0 ? `
            <div class="ag-signatures-section">
              <h3>Signatures</h3>
              ${sigs.map(sig => `
                <div class="ag-signature-entry">
                  <div class="ag-sig-data">${escHtml(sig.signature_data)}</div>
                  <div class="ag-sig-meta">${fmtDateTime(sig.signed_at)} - ${escHtml(sig.signature_type)}</div>
                </div>
              `).join('')}
            </div>
          ` : ''}

          <div class="ag-audit-section">
            <h3>Audit Trail</h3>
            <div class="ag-audit-list">
              ${trail.map(e => `
                <div class="ag-audit-entry">
                  <span class="ag-audit-time">${fmtDateTime(e.created_at)}</span>
                  <span class="ag-audit-action">${escHtml(e.action)}</span>
                  ${e.actor ? `<span class="ag-audit-actor">by ${escHtml(e.actor)}</span>` : ''}
                  ${e.detail ? `<span class="ag-audit-detail">${escHtml(e.detail)}</span>` : ''}
                </div>
              `).join('')}
              ${trail.length === 0 ? '<p class="ag-hint">No activity yet.</p>' : ''}
            </div>
          </div>
        </div>
      </div>`;

    const footer = `
      <button class="btn btn-secondary" id="modal-close-detail">Close</button>
      ${ag.status === 'draft' ? `<button class="btn btn-primary" id="ag-detail-send">Send</button>` : ''}
      ${ag.status === 'sent' || ag.status === 'viewed' ? `<button class="btn btn-primary" id="ag-detail-links">Copy Signing Links</button>` : ''}
      ${ag.status !== 'voided' && ag.status !== 'signed' ? `<button class="btn btn-danger" id="ag-detail-void">Void</button>` : ''}`;

    showModal(modalFrame(`Agreement: ${ag.title}`, content, footer), { wide: true });

    document.getElementById('modal-close')?.addEventListener('click', closeModal);
    document.getElementById('modal-close-detail')?.addEventListener('click', closeModal);

    document.getElementById('ag-detail-send')?.addEventListener('click', () => {
      post<Agreement>(`/api/agreements/${ag.id}/send`, {})
        .then(() => { closeModal(); showToast('Agreement sent', 'ok'); reload(); })
        .catch(e => showToast(String(e), 'err'));
    });

    document.getElementById('ag-detail-void')?.addEventListener('click', () => {
      confirmModal('Void Agreement', 'This is irreversible. Void this agreement?', () => {
        post<Agreement>('/api/agreements/void', { id: ag.id })
          .then(() => { closeModal(); showToast('Agreement voided', 'ok'); reload(); })
          .catch(e => showToast(String(e), 'err'));
      });
    });

    document.getElementById('ag-detail-links')?.addEventListener('click', () => {
      get<AgreementSigningToken[]>(`/api/agreements/${ag.id}/signing-links`).then(tokens => {
        if (tokens.length === 0) {
          showToast('No signing links available', 'warn');
          return;
        }
        const base = `${window.location.origin}${window.location.pathname.replace(/\/[^/]*$/, '')}`;
        const links = tokens.map(t => {
          const rec = recs.find(r => r.id === t.recipient_id);
          const name = rec?.display_name || rec?.email || 'Unknown';
          return `${name}: ${base}/agreements/sign/${t.token}`;
        }).join('\n');
        navigator.clipboard.writeText(links)
          .then(() => showToast('Signing links copied to clipboard', 'ok'))
          .catch(() => showToast(links, 'ok'));
      }).catch(e => showToast(String(e), 'err'));
    });
  }).catch(e => showToast(String(e), 'err'));
}
